import { useAnalysisHistoriesApi } from '@/common/api/modules/article'
import { execSourceBadgeType, execSourceLabel } from '@/common/app/modules/media/articleCreator'
import { dateTimeStr } from '@/common/form/date'
import { ArticleAnalysisHistory } from '@/common/model/media/articleCreator'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import TextButton from '@/components/view/atoms/button/TextButton'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

interface Props {
  siteId: number
  onApply(keyword: string): void
  onViewResult(historyId: number): void
}

// input_data（分析実行時のリクエストパラメータ）に保存されたキーワード一覧を取り出す
const historyKeywords = (history: ArticleAnalysisHistory): string[] => {
  const raw = history.input_data?.keywords
  return Array.isArray(raw) ? raw.map(String) : []
}

// 過去のキーワード分析履歴一覧（記事作成画面のキーワード選定用）
const KeywordAnalysisHistoryList = ({ siteId, onApply, onViewResult }: Props) => {
  const { data, isLoading } = useAnalysisHistoriesApi({
    site_id: siteId,
    analysis_type: 'keyword_analysis',
    page: 1,
    per_page: 20
  })

  if (isLoading) {
    return (
      <Text size={12} color={TextColor.SECONDARY}>
        読み込み中...
      </Text>
    )
  }

  const histories = data?.histories ?? []
  if (histories.length === 0) {
    return (
      <Text size={12} color={TextColor.SECONDARY}>
        分析履歴がありません
      </Text>
    )
  }

  return (
    <div className='border-primary w-full rounded-lg border'>
      {histories.map((h) => {
        const keywords = historyKeywords(h)
        return (
          <div key={h.id} className='border-secondary flex flex-col gap-1 border-t px-4 py-3'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <ItemAlign>
                <Text size={12} color={TextColor.SECONDARY}>
                  {h.created_at ? dateTimeStr(h.created_at) : '-'}
                </Text>
                <Badge size={10} badgeType={execSourceBadgeType(h.execution_source)}>
                  {execSourceLabel(h.execution_source)}
                </Badge>
              </ItemAlign>
              <ItemAlign>
                <TextButton size={12} onClick={() => onViewResult(h.id)}>
                  分析結果を見る
                </TextButton>
                <Button
                  color={ButtonColor.PRIMARY}
                  shrink0
                  disabled={keywords.length === 0}
                  onClick={() => onApply(keywords.join(', '))}
                >
                  このキーワードを使用する
                </Button>
              </ItemAlign>
            </div>
            {keywords.length > 0 && (
              <div className='flex flex-wrap gap-1'>
                {keywords.map((k, i) => (
                  <Badge key={i} size={10} badgeType={BadgeType.KEYWORD}>
                    {k}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default KeywordAnalysisHistoryList
